import { DataTableColDef, GridCellParams, GridColType, SxProps } from '@chronosphereio/chrono-ui';
import { TimeFormat } from '@chronosphereio/core';
import { useMemo } from 'react';
import { useTimeZone } from '@/components';
import {
  BaseStepOptions,
  FieldType,
  FormatOverrides,
  findMatchingOverride,
  findThreshold,
  NumberFormatOverrides,
  NumberFormatThresholdOptionsSchema,
  NumberFormatThresholdStep,
  StepOptions,
  StringFormatOverride,
  StringFormatOverrides,
  TableFormatOverrideKind,
  TableOverridesSchema,
  UnitOptions,
} from '@/core';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { getNumberRenderer } from './table-model';
import { formatTableTimestamp } from './utils/format';
import { normalizeValueFieldName } from './utils/normalize';

const BASE_THRESHOLD_IDENTIFIER = 'BASE_THRESHOLD';

export const EMPTY_TABLE_OVERRIDES: TableOverridesSchema[] = [];

/**
 * Parse a value to a number. Used for String -> Number format overrides.
 * Returns the original value if it's already a number, parses strings to numbers,
 * and returns null for unparseable values.
 */
export const parseStringToNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = parseFloat(trimmed);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const getThresholdClassNameFn = (
  columnId: string,
  thresholds: NumberFormatThresholdOptionsSchema,
  needsValueParsing = false
) => {
  return (params: GridCellParams) => {
    // Get the numeric value - parse if needed for string-to-number conversions
    const numericValue = needsValueParsing ? parseStringToNumber(params.value) : params.value;

    if (typeof numericValue !== 'number') {
      return '';
    }

    const threshold = findThreshold({ thresholds, value: numericValue });

    return getClassNameFromStep(columnId, threshold);
  };
};

const getOverrideClassNameFn = (columnId: string, overrides: StringFormatOverride[]) => {
  return (params: GridCellParams) => {
    if (typeof params.value !== 'string' && typeof params.value !== 'number') {
      return '';
    }

    try {
      const override = findMatchingOverride(`${params.value}`, overrides);

      if (override) {
        return getClassNameFromOverride(columnId, override);
      }
    } catch (_e) {
      // Find matching override can throw if the regex is invalid.
      // Will just swallow this error and not apply any formatting
    }

    return '';
  };
};

const EmptyRenderer: DataTableColDef['renderCell'] = (params) => <div>{params.formattedValue ?? params.value}</div>;

const wrapRendererWithMessageFormatter = (
  renderer: DataTableColDef['renderCell'] | undefined,
  thresholds: NumberFormatThresholdOptionsSchema,
  needsValueParsing = false
) => {
  const actualRenderer = renderer ?? EmptyRenderer;

  const messageFormatter: DataTableColDef['renderCell'] = (params) => {
    // Get the numeric value - parse if needed for string-to-number conversions
    const numericValue = needsValueParsing ? parseStringToNumber(params.value) : params.value;

    if (typeof numericValue === 'number') {
      const threshold = findThreshold({ thresholds, value: numericValue });

      if (threshold.name !== undefined && threshold.name !== '') {
        return <div>{threshold.name}</div>;
      }
    }

    return actualRenderer(params);
  };

  return messageFormatter;
};

const wrapRendererWithOverrideMessageFormatter = (
  renderer: DataTableColDef['renderCell'] | undefined,
  overrides: StringFormatOverride[]
) => {
  const actualRenderer = renderer ?? EmptyRenderer;

  const messageFormatter: DataTableColDef['renderCell'] = (params) => {
    if (typeof params.value === 'string' || typeof params.value === 'number') {
      try {
        const override = findMatchingOverride(`${params.value}`, overrides);

        if (override && override.properties.name && override.properties.name !== '') {
          return <div>{override.properties.name}</div>;
        }
      } catch (_e) {
        // Find matching override can throw if the regex is invalid.
        // Will just swallow this error and not apply any formatting
      }
    }

    return actualRenderer(params);
  };

  return messageFormatter;
};

/**
 * Creates a number renderer that can handle both string and number values.
 * Parses string values to numbers before formatting.
 * Used when String/Time columns are converted to Number format.
 */
const getNumberRendererWithParsing = (unit?: UnitOptions): DataTableColDef['renderCell'] => {
  const baseRenderer = getNumberRenderer(unit);

  return (params: GridCellParams) => {
    // Parse the value if it's a string, otherwise use as-is
    const parsedValue = parseStringToNumber(params.value);
    // Create new params with the parsed value for the base renderer
    return baseRenderer({ ...params, value: parsedValue });
  };
};

const formatNumberColumn = (
  col: DataTableColDef,
  override?: TableOverridesSchema,
  baseThresholds?: NumberFormatThresholdOptionsSchema,
  needsValueParsing = false
) => {
  // If we have the base thresholds, then we will apply those to all the columns.
  // This cellClassName function will get overwritten below if there are threshold overrides.
  // This needs to happen first because it might get overwritten below if there is a column
  // specific override.
  if (baseThresholds) {
    col.cellClassName = getThresholdClassNameFn(BASE_THRESHOLD_IDENTIFIER, baseThresholds, needsValueParsing);
  }

  if (override) {
    const format = override?.properties.format;

    // If we have a custom number formatter, apply a custom renderer
    if (format && isNumberFormatOverrides(format)) {
      if (format.unit !== undefined) {
        // Use parsing renderer only when values might be strings
        const renderNumber = needsValueParsing
          ? getNumberRendererWithParsing(format.unit)
          : getNumberRenderer(format.unit);
        col.renderCell = renderNumber;
      }

      if (format.thresholds) {
        col.cellClassName = getThresholdClassNameFn(override.value, format.thresholds, needsValueParsing);
      }
    }
  }

  // If no custom unit was provided but we still need value parsing, use the parsing renderer
  if (needsValueParsing && !col.renderCell) {
    col.renderCell = getNumberRendererWithParsing();
  }

  // If we have columns specific thresholds, then wrap the renderer to handle message formatting
  // Pass needsValueParsing so threshold checks work on parsed numeric values for string columns
  if (
    override &&
    override.properties.format &&
    isNumberFormatOverrides(override.properties.format) &&
    override.properties.format.thresholds
  ) {
    col.renderCell = wrapRendererWithMessageFormatter(
      col.renderCell,
      override.properties.format.thresholds,
      needsValueParsing
    );
  } else if (baseThresholds) {
    col.renderCell = wrapRendererWithMessageFormatter(col.renderCell, baseThresholds, needsValueParsing);
  }

  return col;
};

const formatStringColumn = (col: DataTableColDef, override?: TableOverridesSchema) => {
  if (!override) {
    return col;
  }

  const format = override.properties.format;

  // If format kind is String, clear any existing formatters and set a default string renderer.
  if (format?.kind === TableFormatOverrideKind.String) {
    // Clear time/number formatters that may have been set during initial column creation.
    delete col.valueFormatter;

    // Only replace renderCell for Time/Number columns being overridden to String.
    // String columns do not have col.type set and should keep their original renderer.
    if (col.type) {
      col.renderCell = (params: GridCellParams) => {
        return <div>{params.value != null ? String(params.value) : '--'}</div>;
      };
    }
  }

  // If we have string-specific overrides (color/message matchers), apply them.
  if (format && isStringFormatOverrides(format) && format.overrides) {
    col = {
      ...col,
      cellClassName: getOverrideClassNameFn(override.value, format.overrides),
      renderCell: wrapRendererWithOverrideMessageFormatter(col.renderCell, format.overrides),
    };
  }

  return col;
};

const formatTimeColumn = (col: DataTableColDef, timeZone?: string, timeFormat?: TimeFormat) => {
  const timeRenderer: DataTableColDef['renderCell'] = (params) => {
    const formattedTime = formatTableTimestamp({ value: params.value, timeZone, timeFormat });
    return <div>{formattedTime}</div>;
  };

  col.renderCell = timeRenderer;
  return col;
};

export const formatColumns = (
  columns: DataTableColDef[],
  columnOverrides: TableOverridesSchema[],
  baseThresholds?: NumberFormatThresholdOptionsSchema,
  timeZone?: string,
  timeFormat?: TimeFormat
) => {
  const columnIdToOverridesMap = columnOverrides.reduce(
    (map, overrides) => {
      map[overrides.value] = overrides;
      const normalized = normalizeValueFieldName(overrides.value);
      if (normalized !== overrides.value) {
        map[normalized] = overrides;
      }

      return map;
    },
    {} as Record<string, TableOverridesSchema>
  );

  return columns.map((col) => {
    // Find the override for this column, if there are any
    const override = columnIdToOverridesMap[col.field];

    // Check if format kind is overridden
    const overrideKind = override?.properties.format?.kind;

    // If overridden to Number, apply number formatting (even if original type was different)
    // If no override and column type is number, apply number formatting as default
    if (overrideKind === TableFormatOverrideKind.Number || (overrideKind === undefined && col.type === 'number')) {
      // Check if column is being converted from String/Time to Number
      // In this case, we need a renderer that can parse string values to numbers
      const needsValueParsing = overrideKind === TableFormatOverrideKind.Number && col.type !== 'number';
      return formatNumberColumn(col, override, baseThresholds, needsValueParsing);
    }

    // If overridden to Time, apply time formatting
    if (overrideKind === TableFormatOverrideKind.Time) {
      return formatTimeColumn(col, timeZone, timeFormat);
    }

    return formatStringColumn(col, override);
  });
};

const stepToStyles = (
  columnId: string,
  step: NumberFormatThresholdStep | NonNullable<NumberFormatThresholdOptionsSchema['base']>
) => {
  const className = getClassNameFromStep(columnId, step);
  const selector = `& .${className}` as const;

  return {
    [selector]: { color: step.color, background: step.background_color },
  } as Record<string, SxProps>;
};

const thresholdsToStyles = (columnId: string, thresholds: NumberFormatThresholdOptionsSchema) => {
  let styles: Record<string, SxProps> = {};

  thresholds.steps?.forEach((step) => {
    styles = {
      ...styles,
      ...stepToStyles(columnId, step),
    };
  });

  if (thresholds.base) {
    styles = { ...styles, ...stepToStyles(columnId, thresholds.base) };
  }

  return styles;
};

const overrideToStyle = (columnId: string, override: StringFormatOverride) => {
  const className = getClassNameFromOverride(columnId, override);
  const selector = `& .${className}` as const;

  return {
    [selector]: { color: override.properties.color, background: override.properties.background_color },
  } as Record<string, SxProps>;
};

const overridesToStyles = (columnId: string, overrides: StringFormatOverride[]) => {
  let styles: Record<string, SxProps> = {};

  overrides.forEach((override) => {
    styles = {
      ...styles,
      ...overrideToStyle(columnId, override),
    };
  });

  return styles;
};

export const generateColumnStyles = (
  columnOverrides: TableOverridesSchema[],
  baseThresholds?: NumberFormatThresholdOptionsSchema
) => {
  let styles: Record<string, SxProps> = {};

  styles = columnOverrides.reduce((calculatingStyle, overrides) => {
    const columnId = overrides.value;

    if (
      overrides.properties.format &&
      isNumberFormatOverrides(overrides.properties.format) &&
      overrides.properties.format.thresholds
    ) {
      calculatingStyle = {
        ...calculatingStyle,
        ...thresholdsToStyles(columnId, overrides.properties.format.thresholds),
      };
    } else if (
      overrides.properties.format &&
      isStringFormatOverrides(overrides.properties.format) &&
      overrides.properties.format.overrides
    ) {
      calculatingStyle = {
        ...calculatingStyle,
        ...overridesToStyles(columnId, overrides.properties.format.overrides),
      };
    }
    return calculatingStyle;
  }, styles);

  if (baseThresholds) {
    styles = {
      ...styles,
      ...thresholdsToStyles(BASE_THRESHOLD_IDENTIFIER, baseThresholds),
    };
  }

  return styles;
};

export const useFormattedColumns = (
  columns: DataTableColDef[],
  columnOverrides: TableOverridesSchema[] | undefined,
  baseThresholds?: NumberFormatThresholdOptionsSchema
) => {
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const formattedColumns = useMemo(() => {
    // Make a copy of the columns so that we don't mutate the original columns.
    // We want to start with the original columns whenever we run this code to apply formatting.
    const columnsCopy = columns.map((col) => ({ ...col }));
    return formatColumns(columnsCopy, columnOverrides ?? EMPTY_TABLE_OVERRIDES, baseThresholds, timeZone, timeFormat);
  }, [columns, columnOverrides, baseThresholds, timeZone, timeFormat]);

  return formattedColumns;
};

export const useColumnStyles = (
  columnOverrides: TableOverridesSchema[] | undefined,
  baseThresholds?: NumberFormatThresholdOptionsSchema
) => {
  return useMemo(
    () => generateColumnStyles(columnOverrides ?? EMPTY_TABLE_OVERRIDES, baseThresholds),
    [columnOverrides, baseThresholds]
  );
};

// Used to distinguish between the base step and the regular steps.
const isStepOption = (maybeStepOption: StepOptions | BaseStepOptions): maybeStepOption is StepOptions =>
  Object.hasOwn(maybeStepOption, 'value');

const nonAlphaNumericRegex = /[^a-zA-Z0-9\-_]/g;

const getClassNameFromStep = (columnId: string, step: StepOptions | BaseStepOptions) => {
  if (isStepOption(step)) {
    return `column-${columnId.replace(nonAlphaNumericRegex, '')}-threshold-${String(step.value).replace(nonAlphaNumericRegex, '_')}`;
  }

  return `column-${columnId.replace(nonAlphaNumericRegex, '')}-threshold-base`;
};

const getClassNameFromOverride = (columnId: string, override: StringFormatOverride) => {
  // Turn a columm and override into a class name
  // For the override value, we replace nonAlphaNumeric characters with _ to hopefully avoid collisions
  // Example: /col/ and col would end up identical if we just removed nonalphanumeric.
  // We could still end up with collisions: col? and col*
  // so we may need to switch to doing a hash to ensure more uniqueness
  return `column-${columnId.replace(nonAlphaNumericRegex, '')}-matcher-${override.matcher?.type || 'ByName'}-${override.value.replace(nonAlphaNumericRegex, '_')}`;
};

export const isNumberFormatOverrides = (overrides?: FormatOverrides): overrides is NumberFormatOverrides => {
  return overrides?.kind === TableFormatOverrideKind.Number;
};

export const isStringFormatOverrides = (overrides?: FormatOverrides): overrides is StringFormatOverrides => {
  return overrides?.kind === TableFormatOverrideKind.String;
};

/**
 * Get override kind from MUI GridColType by checking data frame field type.
 */
export function getTableFormatOverrideKind(gridColType?: GridColType): TableFormatOverrideKind {
  if (gridColType === FieldType.number) {
    return TableFormatOverrideKind.Number;
  }
  if (gridColType === FieldType.time) {
    return TableFormatOverrideKind.Time;
  }
  return TableFormatOverrideKind.String;
}
